<script setup lang="ts">
import { computed, useId } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons';

interface Option {
  label: string;
  value: string;
}

interface Props {
  modelValue?: string;
  label: string;
  options: Option[];
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  autocomplete?: string;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  placeholder: '',
  error: '',
  disabled: false,
  autocomplete: undefined,
});

const emit = defineEmits<{ 'update:modelValue': [value: string] }>();

const selectId = useId();
const errorId = computed(() => `${selectId}-error`);
const hasError = computed(() => props.error.length > 0);

const SELECT_BASE =
  'h-10 w-full px-3 py-2 text-base bg-field rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-offset-0 appearance-none cursor-pointer pr-8';
const SELECT_NORMAL = 'border border-border-strong focus:ring-primary';
const SELECT_ERROR = 'border border-danger focus:ring-danger';
const SELECT_DISABLED = 'opacity-50 cursor-not-allowed';

const selectClass = computed(() => {
  const stateCls = hasError.value ? SELECT_ERROR : SELECT_NORMAL;
  const disabledCls = props.disabled ? ` ${SELECT_DISABLED}` : '';
  return `${SELECT_BASE} ${stateCls}${disabledCls}`.trim();
});

function onChange(e: Event) {
  emit('update:modelValue', (e.target as HTMLSelectElement).value);
}
</script>

<template>
  <div class="flex flex-col">
    <label
      :for="selectId"
      class="text-sm font-normal text-foreground mb-1"
      :class="{ 'text-muted': disabled }"
    >
      {{ label }}
    </label>
    <div class="relative">
      <select
        :id="selectId"
        :value="modelValue"
        :disabled="disabled"
        :autocomplete="autocomplete"
        :aria-invalid="hasError ? 'true' : undefined"
        :aria-describedby="hasError ? errorId : undefined"
        :class="selectClass"
        @change="onChange"
      >
        <option v-if="placeholder" value="" disabled :selected="!modelValue">
          {{ placeholder }}
        </option>
        <option v-for="opt in options" :key="opt.value" :value="opt.value">
          {{ opt.label }}
        </option>
      </select>
      <span
        class="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-muted"
        aria-hidden="true"
      >
        ▾
      </span>
    </div>
    <span
      v-if="hasError"
      :id="errorId"
      class="text-sm text-danger mt-1 flex items-center"
    >
      <FontAwesomeIcon :icon="faCircleExclamation" class="mr-1 size-4" />
      {{ error }}
    </span>
  </div>
</template>
